/**
 * El ticket de una comanda, en bytes ESC/POS.
 *
 * Función PURA: entra la comanda tal cual la devuelve `GET /v1/orders` y salen los
 * bytes. Ni Bluetooth, ni React, ni relojes escondidos — por eso se puede probar
 * entera en Jest sin simular nada, que es donde vive la única lógica de verdad
 * de todo este módulo.
 *
 * El serializador del backend ya trae todo lo que hace falta (`table_label`,
 * `customer_name`, `created_at`, `total`, e items con `quantity`/`product_name`/
 * `notes`), así que aquí no se pide nada nuevo a la API.
 */
import { ANCHO, AVANCE_FINAL, crearTicket, dosColumnas, envolver } from "./escpos";
import { esPeso, cantidadDespachada, cantidadLegible } from "../helpers/products";

/** Sangría de las continuaciones de una línea de producto: debajo del nombre, no del "2x". */
const SANGRIA_PRODUCTO = "   ";

/** Lo que la barra necesita leer de un vistazo: dónde va esto. */
function cabecera(order) {
  if (order?.fulfillment === "serve") {
    return (order.table_label || "Servir en mesa").toUpperCase();
  }
  return "RECOGE EN BARRA";
}

function hora(order, ahora) {
  // La hora de la COMANDA, no la de la impresión: si el ticket sale tarde porque la
  // impresora estaba sin papel, lo que importa es cuándo se pidió.
  const fecha = order?.created_at ? new Date(order.created_at) : ahora;
  if (Number.isNaN(fecha.getTime())) return "";
  return fecha.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
}

/** "18,50" — coma decimal, como en toda la app. */
export function cifra(valor) {
  const numero = Number(valor);
  if (!Number.isFinite(numero)) return "";
  return numero.toFixed(2).replace(".", ",");
}

/** "18,50 EUR". Va "EUR" y no "€" porque el símbolo no es fiable en CP850. */
export function importe(valor) {
  const numero = cifra(valor);
  return numero ? `${numero} EUR` : "";
}

export function construirTicket(order, ahora = new Date()) {
  const t = crearTicket();

  t.inicializar()
    .alineacion("centro")
    .negrita(true)
    .lineas(envolver(cabecera(order)))
    .negrita(false);

  const momento = hora(order, ahora);
  if (momento) t.linea(momento);

  t.separador().alineacion("izquierda");

  for (const item of order?.items || []) {
    const cantidad = Number(item?.quantity) || 0;
    const nombre = item?.product_name || "Producto";
    // A peso imprime GRAMOS y, si hubo báscula, los reales: «250x Lomo» decía
    // al cocinero que sirviera 250 lonchas y el papel es lo que el socio se lleva.
    const etiqueta = esPeso(item) ? `${cantidadLegible(cantidadDespachada(item))} g` : `${cantidad}x`;
    // El nombre en negrita para que el cocinero lo encuentre de un golpe de vista;
    // la nota debajo, sin negrita, indentada bajo el producto al que pertenece.
    // El importe se pega a la derecha de la ÚLTIMA línea del nombre, no de la primera:
    // un producto que ocupa dos renglones dejaría el precio flotando lejos de su total.
    const renglones = envolver(`${etiqueta} ${nombre}`, ANCHO, SANGRIA_PRODUCTO);
    const precio = cifra(item?.subtotal);
    if (precio) {
      const ultima = renglones[renglones.length - 1];
      if (ultima.length + 2 + precio.length <= ANCHO) {
        renglones[renglones.length - 1] = dosColumnas(ultima, precio);
      } else {
        renglones.push(dosColumnas("", precio));
      }
    }
    t.negrita(true).lineas(renglones).negrita(false);
    if (item?.notes) {
      // La sangría se descuenta del ancho ANTES de envolver. Metiéndola después, la
      // primera línea se pasaba de 32 y la partía la impresora por donde le parecía.
      const hueco = ANCHO - SANGRIA_PRODUCTO.length;
      t.lineas(envolver(`* ${item.notes}`, hueco, "  ").map((l) => SANGRIA_PRODUCTO + l));
    }
  }

  t.separador();

  const socio = order?.customer_name || "Socio";
  t.linea(dosColumnas(socio, importe(order?.total)));

  // Sin cuchilla: se rasga a mano y hace falta este colchón para no cortar el texto.
  t.avance(AVANCE_FINAL);

  return t.bytes();
}
